import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Whatshot from '@mui/icons-material/Whatshot';
import classNames from 'classnames';
import pluralize from 'pluralize';
import {
  ActionPlanSchema,
  ActionPlansSchema,
} from '../../../../definitions/schemas';
import { ActionPlanVisibility } from '../../../../enums/ActionPlans';
import { capitalizeWords } from '../../../../lib/String';
import { getCategorizedTasks } from '../../../ActionPlan/Tasks/helper';
import { multilineEllipsis } from '../../../../theme/styles/mixins';
import { pluralizeLabel } from '../../../../lib/Language';
import { RoomActionPlansContext } from './RoomActionPlansContext';
import { useI18n } from '../../../../lib/hook/useI18n';
import EmptySlotBox from '../../../Box/EmptySlotBox';
import Link from '../../../Link';
import Progress from '../../../Progress/Progress';
import Sidebar from '../../View/Sidebar';
import TextWithIcon from '../../../TextWithIcon';
import VisibilityToggle from '../../../Visibility/VisibilityToggle';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    margin: 'auto',
    marginBottom: theme.spacing(1),
  },
  hoverable: {
    '&:hover': {
      backgroundColor: 'var(--background-box-dark)',
    },
  },
  disabled: {
    color: theme.palette.text.disabled,
  },
  name: {
    ...multilineEllipsis(1),
    /**
     * In order to use custom organization fonts, we need to use the h6 variant
     * for the action plan name, but we need to adjust the font size to 16px
     * (body1), because if the body1 variant is used, the font styles won't
     * be applied
     */
    fontSize: theme.typography.body1.fontSize,
  },
  nameCompacted: {
    fontWeight: 'bolder',
    marginRight: theme.spacing(6),
    [theme.breakpoints.down('lg')]: {
      marginRight: 0,
    },
  },
  warning: {
    color: 'var(--warning-color-dark)',
  },
  warningWithBackground: {
    backgroundColor: 'var(--warning-color-light)',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
    textAlign: 'center',
  },
  dueSoon: {
    color: 'var(--warning-color-dark)',
  },
  overdue: {
    color: 'var(--error-color-dark)',
  },
  visibleToggle: {
    position: 'absolute',
    right: theme.spacing(-0.5),
    top: theme.spacing(-1.25),
  },
  changeToggle: {
    border: '1px dashed var(--gray-light)',
    overflow: 'initial',
  },
  disabledActionPlanShared: {
    color: theme.palette.text.disabled,
    opacity: 0.5,
  },
  pointer: {
    cursor: 'pointer',
  },
  wrapInformation: {
    flexDirection: 'row',
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
    },
  },
}));

export interface RoomActionPlansProps {
  actionPlanLabel?: string;
  allowAddingActionPlans?: boolean;
  allowVisibilityChange?: boolean;
  disablePublishWarning?: boolean;
  roomActionPlans: ActionPlansSchema;
  setRoomActionPlans?: React.Dispatch<React.SetStateAction<ActionPlansSchema>>;
  startingRoomActionPlans?: ActionPlansSchema;
}

const RoomActionPlans: React.FC<RoomActionPlansProps> = (
  props: RoomActionPlansProps
): JSX.Element => {
  const { openActionPlanEditor } = useContext(RoomActionPlansContext);

  const { t } = useI18n();
  const classes = useStyles();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('md'));

  const {
    actionPlanLabel,
    allowAddingActionPlans = false,
    allowVisibilityChange,
    disablePublishWarning = false,
    roomActionPlans,
    setRoomActionPlans,
    startingRoomActionPlans = [],
  } = props;

  /**
   * Utility flag to force a re-render for the scroll trigger to work properly
   */
  const [, setMounted] = useState(false);

  useEffect(() => {
    /**
     * Forcing a component re-render to allow the scroll trigger to properly
     * query and attach the scroll event listeners. This is only for the
     * room contact view, as there's no way to get a direct access via ref
     * to the scroll target
     */
    setMounted(true);
  }, []);

  const onOpenEditor = (actionPlan?: ActionPlanSchema) => {
    openActionPlanEditor({
      id: actionPlan?.id,
      visibility: actionPlan?.visibility,
    });
  };

  const onVisibilityChange = (actionPlan: ActionPlanSchema) => {
    if (!setRoomActionPlans) {
      return;
    }

    const newVisibility =
      actionPlan.visibility === ActionPlanVisibility.Internal
        ? ActionPlanVisibility.Shared
        : ActionPlanVisibility.Internal;

    setRoomActionPlans((prev) =>
      prev.map((existingPlan) =>
        existingPlan.id !== actionPlan.id
          ? existingPlan
          : { ...actionPlan, visibility: newVisibility }
      )
    );
  };

  const renderPublishWarning = (isNew: boolean): JSX.Element | undefined => {
    if (!isNew || disablePublishWarning) {
      return undefined;
    }

    return (
      <Box className={classNames(classes.warning)} ml={2} my={1.5}>
        <TextWithIcon
          color="inherit"
          icon={<InfoOutlined color="inherit" fontSize="small" />}
          justifyContent="flex-start"
          text={<Box component="strong">Not yet saved</Box>}
        />
      </Box>
    );
  };

  const renderProgress = (
    progress: number,
    totalTasks: number,
    completedTasks: number,
    dueSoonTasks: number,
    overdueTasks: number,
    compacted?: boolean
  ): JSX.Element => {
    const hasDueTasks = (dueSoonTasks || overdueTasks) > 0;
    let completionNode: React.ReactNode = t('map.status.all_completed');
    if (completedTasks !== totalTasks) {
      completionNode = (
        <Box component="span" minWidth={30} whiteSpace="nowrap">
          <Typography color="textPrimary" component="strong" variant="inherit">
            {completedTasks}
          </Typography>{' '}
          / {totalTasks}
          {!compacted ? ` ${t('map.status.completed')}` : ''}
        </Box>
      );
    }

    const progressNode = (
      <Box
        alignItems="center"
        display="flex"
        flexDirection={compacted ? 'row-reverse' : 'row'}
        mr={compacted ? 2 : 0}
      >
        <Typography color="textSecondary" variant="body2">
          {completionNode}
        </Typography>
        <Box
          flex="1"
          minWidth={compacted ? 60 : 'inherit'}
          ml={compacted ? 0 : 3}
          mr={compacted ? 2 : 0}
        >
          <Progress value={progress} />
        </Box>
      </Box>
    );

    if (!hasDueTasks) {
      return progressNode;
    }

    const pluralTask = (length: number) => pluralizeLabel(length, 'map.task');

    let colorClassName = classes.dueSoon;
    let icon = <Whatshot className={colorClassName} fontSize="small" />;
    let label = `${dueSoonTasks} ${
      compacted ? '' : pluralTask(dueSoonTasks)
    } ${t(`map.status.due_soon`)}`;

    if (overdueTasks > 0) {
      colorClassName = classes.overdue;
      icon = <InfoOutlined className={colorClassName} fontSize="small" />;
      label = `${overdueTasks} ${t(`map.status.overdue`)} ${
        compacted ? '' : pluralTask(overdueTasks)
      }`;
    }

    let content = (
      <>
        {compacted && progressNode}
        {hasDueTasks && (
          <TextWithIcon
            disableTextPadding
            icon={icon}
            text={
              <Typography
                className={colorClassName}
                component="strong"
                variant="inherit"
              >
                {label}
              </Typography>
            }
            variant="body2"
          />
        )}
      </>
    );

    // for mobile view only display dueTask or progress bar
    if (smDown && compacted) {
      content = hasDueTasks ? (
        <TextWithIcon
          disableTextPadding
          icon={icon}
          text={
            <Typography
              className={colorClassName}
              component="strong"
              variant="inherit"
            >
              {label}
            </Typography>
          }
          variant="body2"
        />
      ) : (
        progressNode
      );
    }

    return (
      <Box alignItems="center" display="flex" mr={compacted ? 2 : 0}>
        {content}
      </Box>
    );
  };

  const renderActionPlan = (
    actionPlan: ActionPlanSchema
  ): JSX.Element | undefined => {
    const isActionPlanShared =
      actionPlan && actionPlan.visibility === ActionPlanVisibility.Shared;

    // don't show the action plan if it hasn't been shared with prospects
    if (!allowVisibilityChange && !isActionPlanShared) {
      return undefined;
    }

    const tasks = actionPlan.tasks || [];
    const [completed, dueSoon, overdue] = getCategorizedTasks(tasks);

    const completedTasks = completed.length;
    const dueSoonTasks = dueSoon.length;
    const overdueTasks = overdue.length;

    const totalTasks = tasks.length;
    const progress = totalTasks === 0 ? 0 : (completedTasks / totalTasks) * 100;

    const isNew = !startingRoomActionPlans.some(
      ({ id }) => id === actionPlan.id
    );

    return (
      <Box
        key={actionPlan.id}
        className={classes.container}
        position="relative"
      >
        <Box
          className={classes.pointer}
          onClick={() => onOpenEditor(actionPlan)}
        >
          <Sidebar
            className={classNames(classes.hoverable, {
              [classes.changeToggle]: allowVisibilityChange,
              [classes.disabledActionPlanShared]: !isActionPlanShared,
            })}
            disableGrid
            px={1.5}
            py={1.5}
            width="auto"
          >
            <Box
              className={classes.wrapInformation}
              display="flex"
              justifyContent="space-between"
            >
              <Typography
                className={classNames(classes.name, classes.nameCompacted, {
                  [classes.disabled]: !isActionPlanShared,
                })}
                title={actionPlan.name}
                variant="h6"
              >
                {actionPlan.name}
              </Typography>
              <Box
                alignItems="center"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                {renderProgress(
                  progress,
                  totalTasks,
                  completedTasks,
                  dueSoonTasks,
                  overdueTasks,
                  true
                )}
                <Box alignItems="baseline" display="flex" whiteSpace="nowrap">
                  <Link bold text={t('map.tasks.view_tasks')} variant="body2" />
                </Box>
              </Box>
            </Box>
            {allowVisibilityChange && (
              <Box className={classes.visibleToggle}>
                <VisibilityToggle
                  enabled={isActionPlanShared}
                  onChange={() => onVisibilityChange(actionPlan)}
                  section={actionPlan.name}
                  stopPropagation
                  small
                />
              </Box>
            )}
          </Sidebar>
        </Box>

        {renderPublishWarning(isNew)}
      </Box>
    );
  };

  if (roomActionPlans.length === 0 && allowAddingActionPlans) {
    let label = 'Add Mutual Action Plan';
    if (actionPlanLabel) {
      label = `Add ${capitalizeWords(pluralize(actionPlanLabel, 1))}`;
    }

    return <EmptySlotBox label={label} onOpenEditor={onOpenEditor} />;
  }

  return <>{roomActionPlans.map(renderActionPlan)}</>;
};

export default RoomActionPlans;
